import { useEffect } from 'react';

/**
 * Inject JSON-LD Product structured data for Google rich snippets.
 * Removes itself on unmount to avoid stale schema across navigations.
 */
export default function ProductSchema({ product }) {
  useEffect(() => {
    if (!product) return;

    const siteUrl = window.location.origin;
    const productUrl = `${siteUrl}/product/${product.id}`;
    const lowStock = product.stock > 0 && product.stock < 5;
    const inStock = product.stock > 0 || (product.variants || []).some(v => v.stock > 0);

    const lowestPrice = (product.variants && product.variants.length)
      ? Math.min(...product.variants.map(v => v.price || product.base_price).filter(Boolean))
      : product.base_price;

    const data = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: product.name,
      description: product.description || `${product.name} — EDP bottled at 30% oil concentration (Extrait-level) by JS Fragrance.`,
      image: product.image_url ? [product.image_url] : [],
      brand: {
        '@type': 'Brand',
        name: 'JS Fragrance',
      },
      sku: product.id,
      ...(product.gender ? { audience: { '@type': 'PeopleAudience', suggestedGender: product.gender.toLowerCase() } } : {}),
      offers: {
        '@type': 'Offer',
        url: productUrl,
        priceCurrency: 'PHP',
        price: lowestPrice || 299,
        availability: inStock
          ? (lowStock ? 'https://schema.org/LimitedAvailability' : 'https://schema.org/InStock')
          : 'https://schema.org/OutOfStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: {
          '@type': 'Organization',
          name: 'JS Fragrance',
        },
      },
    };

    const id = `product-jsonld-${product.id}`;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);

    // Per-product Open Graph + Twitter card meta — overrides the global defaults
    // in index.html so when this page is shared on FB/IG/Twitter/WhatsApp the
    // preview shows THIS product's image, name, and price (not the generic site).
    const setMeta = (selector, attrName, value) => {
      let m = document.querySelector(selector);
      if (!m) {
        m = document.createElement('meta');
        const [name, attr] = selector.replace('meta[', '').replace(']', '').split('=');
        m.setAttribute(name, attr.replace(/"/g, ''));
        document.head.appendChild(m);
      }
      m.setAttribute(attrName, value);
    };
    const fullImg = product.image_url
      ? (product.image_url.startsWith('http') ? product.image_url : `${siteUrl}${product.image_url}`)
      : '';
    const titleStr = `${product.name} — JS Fragrance`;
    const descStr = (product.description || `${product.name} — EDP bottled at 30% oil concentration (Extrait-level).`).slice(0, 200);
    setMeta('meta[property="og:title"]', 'content', titleStr);
    setMeta('meta[property="og:description"]', 'content', descStr);
    setMeta('meta[property="og:url"]', 'content', productUrl);
    setMeta('meta[property="og:type"]', 'content', 'product');
    if (fullImg) setMeta('meta[property="og:image"]', 'content', fullImg);
    setMeta('meta[name="twitter:title"]', 'content', titleStr);
    setMeta('meta[name="twitter:description"]', 'content', descStr);
    if (fullImg) setMeta('meta[name="twitter:image"]', 'content', fullImg);
    document.title = titleStr;

    return () => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    };
  }, [product]);

  return null;
}
